import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import styled from 'styled-components';
import { mapApi } from '../services/api';
import { Territory, GridInfo } from '../types';
import { useAuth } from '../hooks/useAuth';
import GridOverlay from './GridOverlay';

const MapWrapper = styled.div`
  height: 100vh;
  width: 100%;
  position: relative;
`;

const InfoPanel = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
`;

const ClaimButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;

  &:hover {
    background: #45a049;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

interface WorldMapProps {
  onTerritorySelect?: (territory: Territory | null) => void;
}

// 网格常量
const GRID_SIZE = 0.0009; // 100米网格

export const WorldMap: React.FC<WorldMapProps> = ({ onTerritorySelect }) => {
  const { user } = useAuth();
  const [selectedGrid, setSelectedGrid] = useState<{ x: number; y: number; lat: number; lng: number } | null>(null);
  const [gridInfo, setGridInfo] = useState<GridInfo | null>(null);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 使用OpenStreetMap作为地图数据源，避免Google Maps在中国的访问问题
  const tileUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  useEffect(() => {
    loadTerritories();
  }, []);

  const loadTerritories = async () => {
    try {
      const response = await mapApi.getTerritories();
      console.log('Loaded territories:', response.data);
      setTerritories(response.data);
    } catch (error) {
      console.error('Failed to load territories:', error);
    }
  };

  const handleGridClick = async (gridX: number, gridY: number, lat: number, lng: number) => {
    console.log('Grid clicked:', { gridX, gridY, lat, lng });

    setSelectedGrid({ x: gridX, y: gridY, lat, lng });
    setIsLoading(true);

    try {
      const response = await mapApi.getGrid(lat, lng);
      setGridInfo(response.data);
    } catch (error) {
      console.error('Failed to get grid info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClaimTerritory = async () => {
    if (!selectedGrid || !user) return;

    try {
      const response = await mapApi.claimTerritory(
        selectedGrid.lat,
        selectedGrid.lng
      );

      console.log('Claim response:', response.data);

      // Update territories list
      setTerritories(prev => {
        const newTerritories = [...prev, response.data];
        console.log('Updated territories:', newTerritories);
        return newTerritories;
      });

      // Update grid info
      setGridInfo({
        gridX: response.data.grid_x,
        gridY: response.data.grid_y,
        isClaimed: true,
        owner: {
          id: response.data.owner_id,
          username: response.data.owner_username,
          doodleIcon: response.data.owner_doodle_icon,
          avatarUrl: response.data.owner_avatar_url,
        },
      });

      alert('占领成功！');
    } catch (error: any) {
      console.error('Failed to claim territory:', error);
      alert(error.response?.data?.message || '占领失败！可能是涂鸦点不足或该区域已被占领。');
    }
  };

  return (
    <MapWrapper>
      <MapContainer
        center={[39.9042, 116.4074]} // Beijing as default center for Chinese users
        zoom={12} // 更高的初始缩放级别以便看到网格
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer url={tileUrl} attribution={attribution} />

        {/* 网格遮罩层 */}
        <GridOverlay
          gridSize={GRID_SIZE}
          onGridClick={handleGridClick}
          territories={territories}
          currentUserId={user?.id}
        />
      </MapContainer>

      {selectedGrid && (
        <InfoPanel>
          <h3>选中网格</h3>
          <p>网格坐标: ({selectedGrid.x}, {selectedGrid.y})</p>
          <p>中心点坐标: ({selectedGrid.lat.toFixed(6)}, {selectedGrid.lng.toFixed(6)})</p>

          {isLoading ? (
            <p>加载中...</p>
          ) : gridInfo ? (
            <div>
              {gridInfo.isClaimed ? (
                <div>
                  <p>✅ 已被占领</p>
                  <p>占领者: {gridInfo.owner?.username}</p>
                  {gridInfo.owner?.doodleIcon && (
                    <img
                      src={gridInfo.owner.doodleIcon}
                      alt="Owner doodle"
                      style={{ width: 32, height: 32 }}
                    />
                  )}
                </div>
              ) : (
                <div>
                  <p>🆓 空白区域</p>
                  {user && (
                    <>
                      <p>占领需要 1 涂鸦点</p>
                      <p>当前涂鸦点: {user.doodlePoints}</p>
                      <ClaimButton
                        onClick={handleClaimTerritory}
                        disabled={user.doodlePoints < 1}
                      >
                        占领此网格
                      </ClaimButton>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </InfoPanel>
      )}
    </MapWrapper>
  );
};